import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CustomerSplitResponse,
  CustomerSplitPreviewResponse,
  SplitCustomerPostRequestBody,
  SplitCustomerPreviewPostRequestBody,
} from '@lio/api-types';
import { useClient } from '@utils/api-client';
import { invalidateAfterCustomerCombine } from '@services/customer/customer.helpers';

// Re-export the published split types so consumers can import them from the service
// barrel (`@services/customer`) alongside the hooks.
export type {
  CustomerSplitAffectedRecord,
  CustomerSplitBorConflict,
  CustomerSplitContactCompare,
  CustomerSplitContactOwner,
  CustomerSplitContactRecord,
  CustomerSplitEligibleQuote,
  CustomerSplitLockedRecord,
  CustomerSplitRecordDisposition,
  CustomerSplitPreviewResponse,
  CustomerSplitResponse,
  SplitCustomerPostRequestBody,
  SplitCustomerPreviewPostRequestBody,
} from '@lio/api-types';

/** Dry-run: returns the full effect of the prospective split for the operator to review. */
export const useSplitCustomerPreview = () => {
  const client = useClient();
  const mutationFn = async (body: SplitCustomerPreviewPostRequestBody) =>
    client.post('customer/split/preview', { json: body }).json<CustomerSplitPreviewResponse>();
  const result = useMutation({ mutationFn });
  return { ...result, previewSplitCustomer: result.mutateAsync };
};

/** Executes the split. */
export const useSplitCustomer = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const mutationFn = async (body: SplitCustomerPostRequestBody) =>
    client.post('customer/split', { json: body }).json<CustomerSplitResponse>();
  const result = useMutation({
    mutationFn,
    // Both customers remain active. Unlike merge, neither customer's matchState changes.
    onSuccess: (data, variables) =>
      invalidateAfterCustomerCombine(
        queryClient,
        [variables.sourceCustomerId, variables.destinationCustomerId],
        data.movedQuoteIds,
      ),
  });
  return { ...result, splitCustomer: result.mutateAsync };
};
